import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';

import {
    clearBarrierName,
    loadBarrierNameDetailsByNameAndContract,
    loadDocumentTypes
} from '../../../store/directusService';
import {
    loadParticipantBarriers,
    loadQuestionnaireAnswersById,
    loadQuestionnaireById,
    updateQuestionnaireAnswers
} from '../../../store/participantService';
import { loadUser } from '../../../store/userService';
import useLeaveBlocker from '../../../utils/hooks/useLeaveBlocker';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import AddDocument from '../../documents/AddDocument';
import GenerateQuestionnairePDF from '../../pdfGenerator/GenerateQuestionnairePDF';
import FormActions from '../../ui/formActions/FormActions';
import ConfirmPrompt from '../../ui/notices/confirmPrompt/ConfirmPrompt';

import SelectBarriersEditor from './barriers/SelectBarriersEditor';
import { themeJson } from './questionnaireAnswerTheme';

import form from '../../../commonStyles/formStyles.module.css';

const QuestionnaireAnswer = ({ row, onClose, roles }) => {

    // LOCAL STATE
    const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
    const [surveyModel, setSurveyModel] = useState(new Model('{}'));
    const [isSucceeded, setIsSucceeded] = useState(false);
    const [generatePdf, setGeneratePdf] = useState(false);
    const [showDocumentDownload, setShowDocumentDownload] = useState(false);
    const [docType, setDocType] = useState(false);
    const [barrierEditor, setBarrierEditor] = useState(false);
    const [barriersProcessed, setBarriersProcessed] = useState(false);
    const [barriersLoaded, setBarriersLoaded] = useState(false);
    const [unselectedBarriers, setUnselectedBarriers] = useState([]);
    const [surveyComplete, setSurveyComplete] = useState(false);
    const [guardActive, setGuardActive] = useState(false);
    const draftAnswersMessage = 'Questionnaire answers have been drafted successfully';
    const completeAnswersMessage = 'Questionnaire answers have been completed successfully';

    // HOOKS
    const dispatch = useDispatch();
    const { isPrompting, proceed, requestLeave } = useLeaveBlocker(guardActive);

    // STORE STATE
    const currentUser = useSelector((state) => state.entities.userService.currentUser);
    const barrierNameDetails = useSelector(
        (state) => state.entities.directusService.barrierNameDetails
    );
    const documentTypes = useSelector((state) => state.entities.directusService.documentTypes);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const currentParticipantBarriers = useSelector(
        (state) => state.entities.participantService.currentParticipantBarriers
    );
    const currentQuestionnaire = useSelector(
        (state) => state.entities.participantService.currentQuestionnaire
    );
    const currentQuestionnaireAnswers = useSelector(
        (state) => state.entities.participantService.currentQuestionnaireAnswers
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        dispatch(clearBarrierName());
        dispatch(loadDocumentTypes());
    }, []);

    useEffect(() => {
        if (documentTypes.length < 1) return;
        setDocType(documentTypes.find((el) => el.name === 'Questionnaire PDF'));
    }, [documentTypes]);

    useEffect(() => {
        if (row.questionnaireId) dispatch(loadQuestionnaireById(row.questionnaireId));
        if (row.id) dispatch(loadQuestionnaireAnswersById(row.id));
    }, [row]);

    useEffect(() => {
        if (currentParticipant.userId) {
            dispatch(loadUser(currentParticipant.userId));
        }
        dispatch(loadParticipantBarriers(currentParticipant.id));
    }, [currentParticipant]);

    useEffect(() => {
        if (Object.keys(currentQuestionnaire)?.length < 1) return;

        const model = new Model(currentQuestionnaire.data);
        model.clearInvisibleValues = 'none';
        model.applyTheme(themeJson);
        model.showQuestionNumbers = true;
        setSurveyModel(model);
    }, [currentQuestionnaire]);

    useEffect(() => {
        if (Object.keys(currentQuestionnaireAnswers)?.length < 1) return;
        surveyModel.data = currentQuestionnaireAnswers?.data;

        // SUPERUSERS can still edit a completed questionnaire
        if (!row.draft && !hasRole([SUPERUSER], roles)) {
            surveyModel.mode = 'display';
        }
    }, [surveyModel, currentQuestionnaireAnswers]);

    useEffect(() => {
        const isReadOnly =
            surveyComplete ||
            (row.draft === false && row.dateCompleted && !hasRole([SUPERUSER], roles)) ||
            (row.draft && !hasRole(acceptedRoles, roles));
        setGuardActive(!isReadOnly);
    }, [row, surveyComplete, roles]);

    useEffect(() => {
        if (successMessage === `Participant barriers loaded`) {
            setBarriersLoaded(true);
        } else if (successMessage === `Questionnaire answers have been completed successfully`) {
            setSurveyComplete(true);
            setGeneratePdf(true);
        } else if (
            successMessage.startsWith(`Document`) &&
            successMessage.endsWith(`has been uploaded`)
        ) {
            onFormExit();
        }
    }, [successMessage]);

    useEffect(() => {
        if (barriersProcessed || !isSucceeded) return;
        if (currentParticipantBarriers.length > 0) {
            if (barrierNameDetails.length > 0) {
                let unselectedBarriers = barrierNameDetails;
                currentParticipantBarriers.filter((el) => {
                    const id = barrierNameDetails.find((entry) => entry.id === el.barrierId)?.id;

                    if (id) {
                        unselectedBarriers = unselectedBarriers.filter((el) => el.id !== id);
                    }
                });
                setUnselectedBarriers(unselectedBarriers);
            }
        } else if (currentParticipantBarriers.length === 0 && barriersLoaded) {
            if (barrierNameDetails.length > 0) {
                setUnselectedBarriers(barrierNameDetails);
            }
        }
    }, [currentParticipantBarriers, barrierNameDetails, isSucceeded]);

    useEffect(() => {
        if (unselectedBarriers.length > 0 && !barriersProcessed) {
            setBarriersProcessed(true);
            onBarrierEditor(true);
        }
    }, [unselectedBarriers]);

    // HELPER FNS
    const createQuestionnairePayload = () => {
        let payload;

        if (Object.keys(currentQuestionnaireAnswers)?.length > 0) {
            payload = {
                ...currentQuestionnaireAnswers
            };
        } else {
            payload = {
                participantId: currentParticipant.id,
                pdfName: currentQuestionnaire.title,
                questionnaireId: currentQuestionnaire.id
            };
        }
        payload.data = surveyModel.data;
        return payload;
    };

    const onCompleteSurvey = () => {
        loadSuggestedBarriers();
        const payload = createQuestionnairePayload();
        payload.draft = false;

        dispatch(updateQuestionnaireAnswers(payload, completeAnswersMessage));
    };

    const loadSuggestedBarriers = () => {
        const barriers = surveyModel
            .getPlainData()
            .filter((el) => el.title.endsWith('Barrier') && el.value);
        const barrierNames = barriers.map((el) => el.name.replaceAll('_', ' '));

        dispatch(
            loadBarrierNameDetailsByNameAndContract(barrierNames, currentParticipant.contractId)
        );
    };

    const onSaveDraft = () => {
        saveDraft();
        onClose();
    };

    const saveDraft = () => {
        const payload = createQuestionnairePayload();
        payload.draft = true;

        dispatch(updateQuestionnaireAnswers(payload, draftAnswersMessage));
    };

    const onBarrierEditor = (setOpen) => {
        setBarrierEditor(setOpen);
    };

    // EVENT HANDLERS
    const onCancel = () => requestLeave(() => onClose());

    const onFormExit = () => onClose();

    const onExit = () => setShowDocumentDownload(false);

    const onPromptYes = () => {
        saveDraft();
        proceed();
    };
    const onPromptNo = () => proceed();

    // EVENT HANDLERS CALLBACKS
    const onPdfGenerated = (isPdfGenerated) => {
        setIsSucceeded(isPdfGenerated);
        setShowDocumentDownload(isPdfGenerated);
        setGeneratePdf(false);
    };

    // RENDER
    return (
        <div>
            <Survey model={surveyModel} onComplete={onCompleteSurvey} />
            <FormActions
                onClose={onSaveDraft}
                onCancel={onCancel}
                disabled={
                    surveyComplete ||
                    (row.draft === false && row.dateCompleted && !hasRole([SUPERUSER], roles)) ||
                    (row.draft && !hasRole(acceptedRoles, roles))
                }
                btnText="Save Draft"
            />
            <SelectBarriersEditor
                open={barrierEditor}
                onBarrierEditor={onBarrierEditor}
                barrierNameDetails={unselectedBarriers}
                currentParticipant={currentParticipant}
            />
            {generatePdf && (
                <GenerateQuestionnairePDF
                    surveyModel={surveyModel}
                    questionnaire={currentQuestionnaire}
                    currentQuestionnaireAnswers={currentQuestionnaireAnswers}
                    currentParticipant={currentParticipant}
                    currentUser={currentUser}
                    onPdfGenerated={onPdfGenerated}
                />
            )}
            {isSucceeded && (
                <div className={form.successMessage}>
                    PDF {currentQuestionnaire.title} downloaded
                </div>
            )}
            {showDocumentDownload && (
                <div>
                    <AddDocument
                        onClose={onExit}
                        onCancel={onExit}
                        isDisabled={!hasRole(acceptedRoles, roles)}
                        btnText="Close"
                        currentDocumentType={docType ? docType : null}
                    />
                </div>
            )}
            <ConfirmPrompt
                isOpen={isPrompting}
                content={
                    'You are about to leave the Questionnaire. Do you want to save your changes?'
                }
                onConfirm={onPromptYes}
                onCancel={onPromptNo}
            />
        </div>
    );
};

QuestionnaireAnswer.propTypes = {
    row: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    roles: PropTypes.arrayOf(PropTypes.string),
};

export default QuestionnaireAnswer;
